import type { SideId } from '@somnhold/content';
import { isBuildableCell, type CampaignState } from '@somnhold/sim';
import type { PatioSlot } from '@somnhold/render';

/**
 * The patio of deeds (docs/SAGAS.md): each saga's trophies on one plinth round the keep, the same plinth for every
 * saga, so a yard full of them reads as one set. Each saga has its cell two out from the keep, evenly round it (the
 * ending, El Párpado, in front, between the well and the faces); one the group built on pushes its plinth in to the
 * keep's yard, which is never built on, a size smaller. A plinth never stands on what the group built. Brann and
 * Hilde's barrels and the weapons of the fallen Unsleeping have a plinth each too, on either side of the front; the
 * Furrows' table and the tomes' shelf stand at the keep's foot, in the yard, with none.
 */
type Group = SideId | 'bets' | 'fallen';
const CELLS: [Group, [number, number], [number, number]][] = [
  // [saga, its cell, its cell in the keep's yard]: the front of the board is +r.
  ['well', [-2, 2], [-1, 1]],
  ['lid', [-1, 2], [-1, 1]],
  ['faces', [0, 2], [0, 1]],
  ['horn', [-2, 0], [-1, 0]],
  ['rails', [2, 0], [1, 0]],
  ['burn', [0, -2], [0, -1]],
  ['shore', [2, -2], [1, -1]],
  ['bets', [-2, 1], [-1, 0]],
  ['fallen', [1, 1], [1, 0]],
];
const YARD: [number, number][] = [[-1, 1], [0, 1], [-1, 0], [1, 0], [0, -1], [1, -1]];

/** Whether a saga left anything in the bastion yet (a plinth with its trophies), or has only begun (a bare one). */
const wonAny = (s: CampaignState, id: SideId): boolean => {
  const x = s.sides?.[id];
  if (!x) return false;
  // Caras prestadas puts up its clue board as soon as it begins.
  if (id === 'faces') return x.chapter > 0 || x.act > 0 || x.clues.length > 0;
  return !!x.done?.[0];
};

/** What else the group has to show: Brann and Hilde's barrels, the fallen's weapons, the table, the shelf or the stone. */
export interface PatioExtras {
  bets: boolean;
  fallen: boolean;
  table: boolean;
  shelf: boolean;
}

export const patioLayout = (s: CampaignState, extras: PatioExtras): Record<string, PatioSlot> => {
  const free = (q: number, r: number): boolean => isBuildableCell(s, q, r) && !s.cells[`${q},${r}`];
  const out: Record<string, PatioSlot> = {};
  const taken = new Set<string>();
  const pushed: [Group, [number, number], boolean][] = [];
  for (const [id, [q, r], yard] of CELLS) {
    if (id === 'bets' || id === 'fallen' ? !extras[id] : !s.sides?.[id]) continue;
    const waiting = id !== 'bets' && id !== 'fallen' && !wonAny(s, id);
    if (free(q, r)) {
      out[id] = { q, r, small: false, waiting };
      taken.add(`${q},${r}`);
    } else pushed.push([id, yard, waiting]);
  }
  // Pushed in: its own yard cell, or the nearest one still free (El Párpado shares the well's and the faces').
  for (const [id, yard, waiting] of pushed) {
    const cell = [yard, ...YARD].find(([q, r]) => !taken.has(`${q},${r}`));
    if (!cell) continue;
    out[id] = { q: cell[0], r: cell[1], small: true, waiting };
    taken.add(`${cell[0]},${cell[1]}`);
  }
  // The table and the shelf at the keep's foot, on the yard's sides if no plinth was pushed in there.
  for (const [id, yard] of [['table', [-1, 0]], ['shelf', [1, 0]]] as const) {
    if (!extras[id]) continue;
    const cell = [yard, ...YARD].find(([q, r]) => !taken.has(`${q},${r}`));
    if (!cell) continue;
    out[id] = { q: cell[0], r: cell[1], small: false, waiting: false, bare: true };
    taken.add(`${cell[0]},${cell[1]}`);
  }
  return out;
};
